import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { auth } from '@/firebase';
import { requestAccountDeletion } from '@/api/account';
import { notifications } from '@/utils/notifications';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { reauthenticateCurrentUser, userRequiresPasswordReauth } from '@/utils/reauthenticateUser';

interface RequestAccountDeletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceName: string;
  memberCount: number;
  onSuccess: () => void;
}

const CONFIRM_PHRASE = 'DELETE';

export const RequestAccountDeletionModal: React.FC<RequestAccountDeletionModalProps> = ({
  isOpen,
  onClose,
  workspaceName,
  memberCount,
  onSuccess,
}) => {
  const { t } = useTranslation('settings');
  const { t: tToast } = useTranslation('notifications');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [password, setPassword] = useState('');
  const needsPassword = userRequiresPasswordReauth(auth.currentUser);

  useEffect(() => {
    if (!isOpen) {
      setConfirmText('');
      setPassword('');
      setError(null);
    }
  }, [isOpen]);

  const confirmReady = confirmText.trim().toUpperCase() === CONFIRM_PHRASE;

  const handleConfirm = async () => {
    if (!confirmReady) return;
    try {
      setIsSubmitting(true);
      setError(null);
      await reauthenticateCurrentUser(needsPassword ? password : undefined);
      await requestAccountDeletion();
      notifications.success(tToast('accountDeletion.scheduled'));
      onSuccess();
      onClose();
    } catch (err: unknown) {
      let message = t('account.dangerZone.requestFailed');
      if (isAxiosError(err)) {
        const data = err.response?.data as { message?: string } | undefined;
        if (data?.message) message = data.message;
      } else if (err instanceof Error) {
        message = err.message;
      }
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ConfirmModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('account.dangerZone.modalTitle')}
      confirmLabel={t('account.dangerZone.confirmLabel')}
      confirmingLabel={t('account.dangerZone.confirming')}
      isConfirming={isSubmitting}
      onConfirm={handleConfirm}
      error={error}
      size="lg"
    >
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground leading-relaxed">
          {t('account.dangerZone.modalIntro', { name: workspaceName })}
        </p>
        {memberCount > 1 && (
          <p className="text-xs font-medium text-red-500/90">
            {t('account.dangerZone.teamWarning', { count: memberCount })}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          {t('account.dangerZone.exportHint')}{' '}
          <Link to="/settings/data-export" className="font-semibold text-primary hover:underline">
            {t('account.dangerZone.exportLink')}
          </Link>
        </p>
        {needsPassword && (
          <div className="space-y-1.5">
            <label htmlFor="delete-account-password" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              {t('account.dangerZone.passwordLabel')}
            </label>
            <input
              id="delete-account-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-foreground focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/10"
            />
          </div>
        )}
        <div className="space-y-1.5">
          <label htmlFor="delete-account-confirm" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {t('account.dangerZone.typeConfirm', { phrase: CONFIRM_PHRASE })}
          </label>
          <input
            id="delete-account-confirm"
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            autoComplete="off"
            className="w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-foreground focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/10"
            placeholder={CONFIRM_PHRASE}
          />
        </div>
      </div>
    </ConfirmModal>
  );
};
